import React, { useMemo, useState } from 'react';
import { ImageUpload } from '@components/image-upload';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { CdnObjectDto, ImageType } from '@api/WalletClient';
import { getCdnImage } from '@components/utils/cdn-image';

export interface WalletImageUploadProps {
    imageType: ImageType;
    label?: string;
    helperText?: string;
    /**
     * Pre-populate preview with an existing CDN image.
     */
    initialImage?: CdnObjectDto | null;
    /**
     * Called after a successful upload with the CDN object returned by the API.
     */
    onUploaded?: (image: CdnObjectDto) => void;
    maxSizeMB?: number;
}

export const WalletImageUpload: React.FC<WalletImageUploadProps> = ({
    imageType,
    label = 'Upload image',
    helperText = 'Drag and drop an image here, or click to select',
    initialImage,
    onUploaded,
    maxSizeMB
}) => {
    const { walletClient } = injectTSDI(Api);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();

    const resolvedPreview = useMemo(() => {
        if (previewUrl) {
            return previewUrl;
        }
        if (initialImage?.bucketName && initialImage?.objectKey) {
            return getCdnImage(
                initialImage.bucketName,
                initialImage.objectKey,
                {
                    resize: { width: 800 }
                }
            );
        }
        return undefined;
    }, [initialImage, previewUrl]);

    const handleUpload = async (file: File) => {
        setLoading(true);
        setError(undefined);

        try {
            const response =
                await walletClient.supportApi.imageControllerUpload(
                    { type: imageType },
                    { file }
                );

            onUploaded?.(response.data);
            setPreviewUrl(URL.createObjectURL(file));
        } catch (err: any) {
            console.error('Wallet image upload failed:', err);
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Failed to upload image. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <ImageUpload
            onUpload={handleUpload}
            loading={loading}
            error={error}
            previewUrl={resolvedPreview}
            label={label}
            helperText={helperText}
            maxSizeMB={maxSizeMB}
        />
    );
};
